import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Loading } from '@common_components/loading.component';

import { getUpcomingWeekdays } from './tableUtils.component';
import EventManagerDays from './eventManagerDays.component';
import EventManagerWeeks from './eventManagerWeeks.component';
import EventCompletionModal from 'components/modals/events/eventCompletionModal.component';
import EngagementModal from 'components/modals/engagements/engagementModal.component';

import { User, UserWithAvatar } from 'interfaces/user.interface';
import { EventCommitment, PutEventCommitment } from 'interfaces/types.interface';
import { EventManager, EventManagerWithPosition } from 'interfaces/eventManager.interface';

import { ArchiveBoxIcon, dayjs, FORMAT_DATE_API, JSONObject, RootState, useMqttSubscription, usePersistantState, UUID } from '@umanforge/common';

import { KEY } from 'constants/local_storage_key.constant';
import { DIVIDE_FOR_WEEK, ZOOM_LEVEL } from 'constants/eventManager.constant';

import { GlobalService } from 'services/global.service';
import { EngagementService } from 'services/engagements.service';
import { EventManagerService } from 'services/eventManagers.service';
import { EventService } from 'services/events.service';

import EmptyState from 'components/common/EmptyState.component';
import { Dayjs } from 'dayjs';
import { EventManagerNavigationBar } from 'features/header_navigation_event_manager/components/eventManagerNavigationBar.component';
import { VisualisationService } from 'services/visualisation.service';
import { EventManagerCharts } from 'components/charts/eventManagerCharts.component';


interface EventManagerContentProps {
  setSelectedSectorId: ( newSectorId:UUID | undefined ) => void;
  eventManager: EventManager | null;
  updateEventManager: ( eventManagerId: UUID | undefined ) => Promise<void>;
  isArchived: boolean;
  setIsArchived: React.Dispatch<React.SetStateAction<boolean>>;
  themeHightlighted?: string;
  navbarPosition?: 'left' | 'top';
  onEventManagerUpdate?: ( eventManager: EventManager ) => void;
  isViewMode?: boolean;
  isAdminOfEventManager: boolean;
  eventIdToOpen?: string | null;
  engagementIdToOpen?: string | null;
  onEventOpened?: () => void;
  onChartsToggle?: ( showCharts: boolean ) => void;
}

export const EventManagerContent: React.FC<EventManagerContentProps> = ( {
  setSelectedSectorId,
  eventManager,
  updateEventManager,
  isArchived,
  setIsArchived,
  themeHightlighted,
  navbarPosition = 'left',
  onEventManagerUpdate,
  isViewMode,
  isAdminOfEventManager,
  eventIdToOpen,
  engagementIdToOpen,
  onEventOpened,
  onChartsToggle,
} ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  //--------------- useState ---------------\\
  const [ days, setDays ] = useState<dayjs.Dayjs[]>( [] );
  const [ absenceDaysByUser, setAbsenceDaysByUser ] = useState<{ [userId: UUID]: dayjs.Dayjs[] }>( {} );

  const [ tableId, setTableId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_EVENT_MANAGER_ID, undefined );

  const [ dataEventManager, setDataEventManager ] = useState<Map<string, Map<string, EventCommitment[]>>>( new Map() );
  const [ participants, setParticipants ] = useState<( UserWithAvatar & { eventManagersId: UUID[] } )[]>( [] );

  const [ eventClick, setEventClick ] = useState<EventCommitment>();
  const [ eventDroped, setEventDroped ] = useState<EventCommitment | null>();

  const [ isHoverArchived, setIsHoverArchived ] = useState<boolean>( false );
  const [ isDragging, setIsDragging ] = useState<boolean>( false );
  const [ eventClicked, setEventClicked ] = useState<boolean>( false );

  const [ calendarItemPersisted, setCalendarItemPersisted ] = usePersistantState<number>( KEY.CALENDAR_ITEM_ZOOM, 5 );
  const [ calendarItem, setCalendarItem ] = useState<number>( calendarItemPersisted );

  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ isLoading, setIsLoading ] = useState( false );
  const [ isPopupCreateEventVisible, setIsPopupCreateEventVisible ] = useState( false );
  const [ isPopupCompletedEventVisible, setIsPopupCompletedEventVisible ] = useState( false );

  const [ dateClicked, setDateClicked ] = useState<string | undefined>( undefined );
  const [ selectedUserId, setSelectedUserId ] = useState<string | undefined>( undefined );
  const [ showCharts, setShowCharts ] = useState<boolean>( false );
  const [ chartsSectionsRefreshKey, setChartsSectionsRefreshKey ] = useState( 0 );

  //--------------- useEffect ---------------\\
  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshEventManager();
    }
  }, [ tableId ] );

  useEffect( () => {
    setCalendarItem( calendarItemPersisted );
  }, [ calendarItemPersisted ] );

  useEffect( () => {
    if ( !isFirstRender && eventManager != null ) {
      const visibleDays = Object.values( eventManager.daysToShow || {} ).filter( day => { return day === true; } ).length;
      const defaultZoom = eventManager.calendarItem ?? ( visibleDays > 0 ? visibleDays : ZOOM_LEVEL.THREE_DAYS );
      setCalendarItem( defaultZoom );
      setCalendarItemPersisted( defaultZoom );
    }
  }, [ eventManager, isFirstRender ] );

  useEffect( () => {
    if ( !isFirstRender && eventManager != null ) {
      refreshDays();
      let count = calendarItem;
      if ( calendarItem > ZOOM_LEVEL.TEN_DAYS ) {
        const numberOfDayWeek = Object.values( eventManager.daysToShow ).filter( dayToShow => { return dayToShow === true; } ).length;
        const numberOfWeekToSee = calendarItem / DIVIDE_FOR_WEEK;
        count = numberOfDayWeek * numberOfWeekToSee;
      }
      setDays( getUpcomingWeekdays( dayjs().startOf( 'day' ), count, eventManager.daysToShow ) );
      if ( onEventManagerUpdate ) {
        onEventManagerUpdate( eventManager );
      }
    } else {
      const today = dayjs().startOf( 'day' );
      setDays( [ today, today ] );
      setDataEventManager( new Map() );
      setParticipants( [] );
      setAbsenceDaysByUser( {} );
    }
  }, [ eventManager, calendarItem ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshDays();
    }
  }, [ isArchived ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      if ( !isFirstRender && tableId != null && days.length > 0 ) {
        refreshEventManagerLinks();
      }
    }
  }, [ days ] );

  useEffect( () => {
    if ( !isFirstRender && eventDroped ) {
      if ( eventDroped.completed ) {
        archivedDropedEvent( eventDroped );
      } else {
        setIsPopupCompletedEventVisible( true );
      }
    }
  }, [ eventDroped ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      if ( days.length > 0 && !isPopupCreateEventVisible && !eventClicked ) {
        refreshParticipantsAndEngagements();
      }
    }
  }, [ isPopupCreateEventVisible, eventClicked ] );

  useEffect( () => {
    if ( eventIdToOpen && !eventClicked ) {
      const fetchAndOpenEvent = async () => {
        try {
          const eventData = await EventService.getEventById( eventIdToOpen, token, dispatch );
          if ( eventData && eventData.engagements && eventData.engagements.length > 0 ) {
            // Prefer engagementIdToOpen if provided, otherwise fallback to first engagement
            const chosenEngagement = engagementIdToOpen
              ? eventData.engagements.find( e => {return e.id === engagementIdToOpen;} )
              : eventData.engagements[ 0 ];
            const event: EventCommitment = {
              id: chosenEngagement?.id || eventIdToOpen,
              eventId: eventIdToOpen,
              participants: [],
              eventManagerIds: chosenEngagement?.eventManagerIds || [],
              deliveryDate: chosenEngagement?.deliveryDate || '',
              completed: Boolean( chosenEngagement?.completed ),
              completedDate: chosenEngagement?.completedDate || '',
              archived: Boolean( chosenEngagement?.archived ),
              eventTitle: eventData.event?.title || '',
              color: chosenEngagement?.color || '',
              isEventRecurring: eventData.event?.recurring ? 1 : 0,
              haveNote: Array.isArray( eventData.notes ) && eventData.notes.length > 0,
              haveDocument: Array.isArray( eventData.documents ) && eventData.documents.length > 0,
              numberOfLateDays: 0,
              complatedPercentage: 0,
              authorFullName: '',
              creationDate: eventData.event?.createdDate || ''
            };
            setEventClick( event );
            setEventClicked( true );
            if ( onEventOpened ) {
              onEventOpened();
            }
          }
        } catch ( error ) {
          console.error( 'Error fetching event:', error );
        }
      };
      fetchAndOpenEvent();
    }
  }, [ eventIdToOpen, engagementIdToOpen, eventClicked, onEventOpened, token, dispatch ] );

  //--------------- Mqtt ---------------\\
  useMqttSubscription( ( msg: JSONObject, category: string | null ) => {
    const payload = msg.payload as { eventId?: string };
    if ( category === 'event_manager' && payload.eventId != null ) {
      refreshEventManagerLinks();
    }
  } );

  //--------------- Functions ---------------\\
  const wrappedSetCalendarItem = ( value: number | ( ( prevState: number ) => number ) ) => {
    const newValue = typeof value === 'function' ? value( calendarItem ) : value;
    setCalendarItem( newValue );
    setCalendarItemPersisted( newValue );
  };

  const getUpdatedSelectedTableId = ( allEventManagers: EventManagerWithPosition[] ) => {
    let newSelectedTableId: UUID | undefined = tableId;
    if ( allEventManagers.length > 0 ) {
      if ( !allEventManagers.some( table => { return table.id === tableId; } ) ) {
        newSelectedTableId = allEventManagers[ 0 ].id;
      }
    } else {
      newSelectedTableId = undefined;
    }
    return newSelectedTableId;
  };

  const setNewDays = ( date: dayjs.Dayjs ) => {
    if ( eventManager ) {
      setDays( getUpcomingWeekdays( date, calendarItem, eventManager.daysToShow ) );
    }
  };

  const getParticipantById = ( id: string ): ( UserWithAvatar & { eventManagersId: UUID[]; } ) => {
    return (
      participants.find( participant => {
        return participant.id === id;
      } ) || { id: '', firstname: '', lastname: '', eventManagersId: [] }
    );
  };

  const isDateInMap = ( dateToCheck: dayjs.Dayjs, userId: UUID ): boolean => {
    const absenceDays = absenceDaysByUser[ userId ];
    let isAbsenceDay = false;
    if ( absenceDays && absenceDays.length > 0 ) {
      isAbsenceDay = absenceDays.some( date => { return date.isSame( dayjs( dateToCheck ), 'day' ); } );
    }
    return isAbsenceDay;
  };

  const isDateInWeekMap = ( startWeekToCheck: dayjs.Dayjs, userId: UUID ): boolean => {
    let isClosedWeek = false;
    if ( eventManager ) {
      const numberOfWeekDay = Object.values( eventManager.daysToShow ).filter( dayToShow => { return dayToShow === true; } ).length;
      const startWeek = dayjs( startWeekToCheck ).startOf( 'week' );
      const datesToCheck = getUpcomingWeekdays( startWeek, numberOfWeekDay, eventManager.daysToShow );
      const numberOfClosedWeekDay = datesToCheck
        .map( dateToCheck => {
          const absenceDays = absenceDaysByUser[ userId ];
          let isAbsenceDay = false;
          if ( absenceDays && absenceDays.length > 0 ) {
            isAbsenceDay = absenceDays.some( date => { return date.isSame( dateToCheck, 'day' ); } );
          }
          return isAbsenceDay;
        } )
        .filter( isClosed => { return isClosed === true; } ).length;

      isClosedWeek = numberOfClosedWeekDay === numberOfWeekDay;
    }
    return isClosedWeek;
  };

  const getNextDayNotClosed = ( startDay: dayjs.Dayjs, userId: UUID ): dayjs.Dayjs => {
    const absenceDays = absenceDaysByUser[ userId ];
    let day = dayjs().startOf( 'day' );
    if ( !startDay.isBefore( dayjs(), 'day' ) ) {
      day = startDay.startOf( 'day' );
    }
    if ( eventManager && absenceDays && absenceDays.length > 0 ) {
      const numberOfWeekDay = Object.values( eventManager.daysToShow ).filter( dayToShow => { return dayToShow === true; } ).length;
      const datesToCheck = getUpcomingWeekdays( day.startOf( 'week' ), numberOfWeekDay, eventManager.daysToShow )
        .sort( ( date1, date2 ) => { return date1.isAfter( date2 ) ? 1 : -1;  } )
        .filter( date => { return date.isAfter( day ) || date.isSame( day ); } );
      const findedDay = datesToCheck.find( dateToCheck => {
        return !absenceDays.some( date => { return date.isSame( dateToCheck, 'day' ); } );
      } );
      if ( findedDay ) {
        day = findedDay;
      }
    }
    return day;
  };

  const getWeeks = () => {
    const weeks = [];
    if ( eventManager ) {
      const numberOfWeek = calendarItem / DIVIDE_FOR_WEEK;
      for ( let weekNumber = 0; weekNumber < numberOfWeek; weekNumber += 1 ) {
        const startWeek = days[ 0 ].add( weekNumber, 'week' ).startOf( 'week' );
        weeks.push( startWeek );
      }
    }
    return weeks;
  };

  const refreshEventManager = async () => {
    setIsLoading( true );
    await updateEventManager( tableId );
    setIsLoading( false );
    setChartsSectionsRefreshKey( k => { return k + 1; } );
  };

  const refreshEventManagerLinks = async () => {
    if ( eventManager ) {
      await refreshParticipantsAndEngagements();
      await refreshMissingDays();
    }
  };

  const refreshDays = () => {
    setNewDays( dayjs() );
  };

  const getUpdatedEngagements = async ( eventManagerId: UUID, currentParticipants: ( User & { eventManagersId: UUID[]; } )[], startDate: Dayjs, endDate: Dayjs ) => {
    const result: EventCommitment[] = [];
    if ( days.length > 0 ) {
      if ( isViewMode ) {
        result.push( ...( await VisualisationService.getEngagements( eventManagerId, startDate, endDate, visualisationToken, dispatch ) ) );
      } else {
        result.push( ...( await EngagementService.getEngagements( eventManagerId, startDate, endDate, token, dispatch ) ) );
      }
    }
    return GlobalService.transformEventCommitmentsToMap( result, currentParticipants, days );
  };

  const getUpdatedMissingDays = async ( eventManagerId: UUID, startDate: Dayjs, endDate: Dayjs ) => {
    let result: { [userId: UUID]: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs; }[]; } = {};
    if ( days.length > 0 ) {
      if ( isViewMode ) {
        result = await VisualisationService.getMissingDayOfEventManagerByfUser( eventManagerId, startDate, endDate, visualisationToken, dispatch );
      } else {
        result = await EventManagerService.getMissingDayOfEventManagerByfUser( eventManagerId, startDate, endDate, token, dispatch );
      }
    }
    return GlobalService.transformRangedDateToListDate( result );
  };

  const refreshParticipantsAndEngagements = async () => {
    if ( eventManager ) {
      const updatedParticipants = EventManagerService.getOrderedParticipantsOfEventManager( eventManager, me );
      if ( updatedParticipants.length > 0 ) {
        const range = getRangedDates();
        const updatedEngagements = await getUpdatedEngagements( eventManager.id, updatedParticipants, range.startDate, range.endDate );
        setParticipants( updatedParticipants );
        setDataEventManager( updatedEngagements );
      }
    }
  };

  const refreshMissingDays = async () => {
    if ( eventManager ) {
      const range = getRangedDates();
      const updatedMissingDays = await getUpdatedMissingDays( eventManager.id, range.startDate, range.endDate );
      setAbsenceDaysByUser( updatedMissingDays );
    }
  };

  const getRangedDates = () => {
    const startDate = days.reduce( ( min, date ) => { return ( date.isBefore( min ) ? date : min ); } );
    const endDate = days.reduce( ( max, date ) => { return ( date.isAfter( max ) ? date : max ); } );
    return { startDate, endDate };
  };

  const archivedDropedEvent = async ( engagementDrag: EventCommitment, forceCompleted?: boolean ) => {
    if ( engagementDrag.id ) {
      try {
        const body: PutEventCommitment = { archived: true };
        if ( forceCompleted ) {
          body.completed = true;
        }
        const isUpdate = await EngagementService.updateEngagementAPI( engagementDrag.id, body, token, dispatch );
        setEventDroped( null );
        if ( isUpdate ) {
          refreshEventManagerLinks();
        }
      } catch ( error ) {
        console.error( 'Error archiving engagement:', error );
        setEventDroped( null );
      }
    }
  };

  //--------------- Events ---------------\\
  const onEventManagersChanged = ( eventManagers: EventManagerWithPosition[] ) => {
    if ( !isFirstRender ) {
      const newTableId = getUpdatedSelectedTableId( eventManagers );
      if ( newTableId != tableId ) {
        setTableId( newTableId );
      }
    }
  };

  const onDragOverArchived = ( event: React.DragEvent<HTMLDivElement> ) => {
    event.stopPropagation();
    event.preventDefault();
    setIsHoverArchived( true );
  };

  const onDragLeaveArchived = () => {
    setIsHoverArchived( false );
  };

  const onDropArchived = ( dropedEvent: React.DragEvent<HTMLDivElement> ) => {
    const event = JSON.parse( dropedEvent.dataTransfer.getData( 'engagement' ) ) as EventCommitment;
    event.deliveryDate = dayjs( event.deliveryDate ).format( FORMAT_DATE_API );
    setEventDroped( event );
  };

  return (
    <>
      {
        isLoading ?
          <div className='flex w-screen h-screen items-center justify-center pb-10'>
            <Loading size={ 150 }/>
          </div>
          :
          <div
            className={ clsx(
              'flex flex-col min-h-screen mx-4 overflow-visible',
              isViewMode ? '' : navbarPosition === 'top' ? 'pt-20' : 'ml-24 pt-2'
            ) }
          >
            <EventManagerNavigationBar
              setSelectedSectorId={ setSelectedSectorId }
              eventManager={ eventManager }
              onEventManagersChanged={ onEventManagersChanged }
              eventManagerId={ tableId }
              setEventManagerId={ setTableId }
              isArchived={ isArchived }
              setIsArchived={ setIsArchived }
              isViewMode={ isViewMode ?? false }
              calendarItem={ calendarItem }
              setCalendarItem={ wrappedSetCalendarItem }
              days={ days }
              setDays={ setDays }
              setNewDays={ setNewDays }
              isAdminOfEventManager={ isAdminOfEventManager }
              refreshEventManager={ refreshEventManager }
              showCharts={ showCharts }
              onToggleCharts={() => {
                setShowCharts( prev => {
                  const next = !prev;
                  // Si on passe en vue SafetyCross, on désactive le mode archivage
                  if ( next ) {
                    setIsArchived( false );
                  }
                  onChartsToggle?.( next );
                  return next;
                } );
              }}
            />
            <div className='h-full w-full my-2'>
              {
                showCharts ? (
                  tableId && eventManager ? (
                    <EventManagerCharts
                      eventManager={ eventManager }
                      onValidate={() => { void refreshEventManagerLinks(); }}
                      isAdminOfEventManager={isAdminOfEventManager}
                      sectionsRefreshKey={chartsSectionsRefreshKey}
                    />
                  ) : (
                    <div className='flex w-full justify-center mt-20'>
                      <EmptyState type='event_managers'/>
                    </div>
                  )
                ) : (
                  tableId && eventManager ? (
                    <>
                      {
                        calendarItem <= ZOOM_LEVEL.TEN_DAYS ?
                          <EventManagerDays
                            days={ days }
                            isViewMode={ isViewMode }
                            calendarItem={ calendarItem }
                            dataEventManager={ dataEventManager }
                            getParticipantById={ getParticipantById }
                            isDateInMap={ isDateInMap }
                            setEventClick={ setEventClick }
                            setEventClicked={ setEventClicked }
                            setIsDragging={ setIsDragging }
                            isArchived={ isArchived }
                            isDragging={ isDragging }
                            eventManager={ eventManager }
                            themeHightlighted={ themeHightlighted }
                            isPopupCreateEventVisible={ isPopupCreateEventVisible }
                            setIsPopupCreateEventVisible={ setIsPopupCreateEventVisible }
                            setSelectedUserId={ setSelectedUserId }
                            refreshEngagementsOfEventManagerForDays={ refreshParticipantsAndEngagements }
                            isAdminOfEventManager={ isAdminOfEventManager }
                            getNextDayNotClosed={ getNextDayNotClosed }
                            setDateClicked={setDateClicked}
                          />
                          :
                          <EventManagerWeeks
                            weeks={ getWeeks() }
                            isViewMode={ isViewMode }
                            dataEventManager={ dataEventManager }
                            getParticipantById={ getParticipantById }
                            isDateInMap={ isDateInWeekMap }
                            setEventClick={ setEventClick }
                            setEventClicked={ setEventClicked }
                            setIsDragging={ setIsDragging }
                            isArchived={ isArchived }
                            isDragging={ isDragging }
                            eventManager={ eventManager }
                            themeHightlighted={ themeHightlighted }
                            isPopupCreateEventVisible={ isPopupCreateEventVisible }
                            setIsPopupCreateEventVisible={ setIsPopupCreateEventVisible }
                            setSelectedUserId={ setSelectedUserId }
                            refreshEngagementsOfEventManagerForDays={ refreshParticipantsAndEngagements }
                            isAdminOfEventManager={ isAdminOfEventManager }
                            getNextDayNotClosed={ getNextDayNotClosed }
                            setDateClicked={setDateClicked}
                          />
                      }
                    </>
                  ) : (
                    <div className='flex w-full justify-center mt-20'>
                      <EmptyState type='event_managers'/>
                    </div>
                  )
                )
              }
            </div>

            {
              ( eventClicked || isPopupCreateEventVisible ) && (
                <EngagementModal
                  {...( eventClicked ? { engagementClicked: eventClick } : {} )}
                  isViewMode={ isViewMode }
                  onClose={() => {
                    setEventClicked( false );
                    setIsPopupCreateEventVisible( false );
                  }}
                  isAdminOfEventManager={isAdminOfEventManager}
                  dateCLicked={ dateClicked }
                  userClickedId={ selectedUserId }
                />
              )
            }

            <EventCompletionModal
              isVisible={eventDroped !== null && isPopupCompletedEventVisible}
              eventDroped={eventDroped}
              onClose={() => {
                setIsPopupCompletedEventVisible( false );
                setEventDroped( null );
              }}
              onConfirmNo={() => {
                if ( eventDroped ) {
                  archivedDropedEvent( eventDroped, false );
                }
                setIsPopupCompletedEventVisible( false );
              }}
              onConfirmYes={() => {
                if ( eventDroped ) {
                  archivedDropedEvent( eventDroped, true );
                }
                setIsPopupCompletedEventVisible( false );
              }}
            />

            {
              isDragging && (
                <div className='fixed bottom-0 left-0 pl-24 pr-6 h-[5-rem] w-full z-20'>
                  <div
                    className={`rounded-md m-2 h-16 bg-purple-300 trans-fast shadow-md w-full border-2 border-purple-300 flex-center gap-4 text-xl font-bold font-comfortaa text-purple-700 ${isHoverArchived && 'bg-purple-400'
                    }`}
                    onDragOver={ onDragOverArchived }
                    onDragLeave={ onDragLeaveArchived }
                    onDrop={ onDropArchived }
                  >
                    <ArchiveBoxIcon />
                    <p>{ t( 'translation:archive' ) }</p>
                  </div>
                </div>
              )
            }
          </div>
      }
    </>
  );
};
